import { useEffect, useState } from "react";

/**
 * Returns the id of the section currently occupying the top of the viewport.
 *
 * IntersectionObserver alone gives you "is visible", which is ambiguous when
 * three short sections are on screen at once. So we observe everything and then
 * pick the last heading that has crossed the offset line — the one you are
 * actually reading.
 */
export const useScrollSpy = (ids: string[], offset = 140): string => {
  const [activeId, setActiveId] = useState<string>(ids[0] || "");

  useEffect(() => {
    if (!ids.length) return;

    let frame = 0;

    const read = () => {
      frame = 0;
      let current = ids[0];

      for (const id of ids) {
        const el = document.getElementById(id);
        if (!el) continue;
        if (el.getBoundingClientRect().top - offset <= 0) {
          current = id;
        }
      }

      // At the very bottom nothing further can cross the line, so make sure the
      // final section wins — otherwise the last TOC entry never highlights.
      const atBottom =
        window.innerHeight + window.scrollY >=
        document.documentElement.scrollHeight - 4;
      if (atBottom) current = ids[ids.length - 1];

      setActiveId(current);
    };

    const onScroll = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(read);
    };

    read();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [ids, offset]);

  return activeId;
};

export default useScrollSpy;
